<template>
  <div>
    <div
      :class="componentClasses"
    >
      <header class="section-head">
        <Title size="4_5">
          {{ dossierSection.label }}
        </Title>

        <Toggle
          v-if="!isOtherSection"
          :checked="isActive"
          @toggle="next => emit('toggle', next, dossierSection.value)"
        />
      </header>

      <Stack
        v-if="isActive"
        class="section-body"
        gap="4"
      >
        <Stack
          v-if="sectionItems.length"
          class="section-items"
          gap="1"
        >
          <div
            v-for="item in sectionItems"
            :key="item.id"
            class="section-item"
          >
            <p class="item-name">
              {{ item.custom_document_name || item.document_type }}

              <Badge
                v-if="item.custom_document_name"
                status="info"
              >
                Custom
              </Badge>
            </p>

            <div class="item-required">
              <span class="item-required-label">Required</span>
              <Toggle
                :checked="item.is_required"
                @change="emit('item-requirement', dossierSection.value, item.id)"
              />
            </div>

            <div
              class="item-delete"
              title="Delete this document"
            >
              <IconButton
                class="item-delete-button"
                icon="fas fa-trash-alt"
                @click="emit('item-deletion', dossierSection.value, item.id)"
              />
            </div>
          </div>
        </Stack>

        <DocumentTypesAddition
          :already-present="sectionItems"
          @addition="documentType => emit('item-addition', dossierSection.value, documentType)"
          @new="documentName => emit('new-item', dossierSection.value, null, documentName)"
        />
      </Stack>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Badge, IconButton, Stack, Title, Toggle } from '@stakeui';
import DocumentTypesAddition from './DocumentTypesAddition.vue';

const props = defineProps({
  dossierSection: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  currentSections: {
    type: Object,
    default: () => ({}),
  },
  hasActiveSections: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'toggle',
  'item-addition',
  'item-deletion',
  'item-requirement',
]);

const isOtherSection = computed(() => props.dossierSection.value === 'other');
const isActive = computed(() => props.dossierSection.value in props.currentSections || isOtherSection.value);
const sectionItems = computed(() => {
  const section = props.currentSections?.[props.dossierSection?.value];
  return section?.items ?? [];
});

const componentClasses = computed(() => [
  'section-element',
  isOtherSection.value && 'is-other-section',
  props.hasActiveSections && 'has-active-sections',
  !props.hasActiveSections && 'has-no-active-sections',
  isActive.value && 'is-active',
  !isActive.value && 'not-active',
]); </script>

<style scoped>
.section-element {
  border-radius: 0.75em;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity;

  &.is-other-section {
    background-color: red;
  }

  &.has-no-active-sections {
    opacity: 1 !important;
  }

  &.not-active {
    opacity: 0.2;

    &:hover {
      opacity: 1;
    }
  }

  &.is-active {
    background-color: #fcfcfd;
    box-shadow: var(--box-inner-shadow);

    .section-head {
      background-color: #fff;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.04),
        0 -1px 0 0 rgba(0, 0, 0, 0.08) inset;
      padding: 0.75em 1em;
    }
  }
}

.section-head {
  border-radius: 0.5em 0.5em 0 0;
  padding: 0.25em 1em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition-duration: var(--stk-transition-duration);
  transition-property: padding;
}

.section-body {
  padding: 1.25em;
}

.section-item {
  background-color: #f3f4f6;
  border-radius: 0.5em;
  align-items: center;
  padding: 0.25em 0.5em;
  display: flex;
  gap: 0.75em;
}

.item-name {
  color: var(--stk-text-strong);
  font-weight: 500;
  flex-grow: 1;
}

.item-required {
  display: flex;
  font-size: 0.875em;
  align-items: center;
  gap: 0.25em;
}

.item-required-label {
  font-size: 0.75rem;
  color: var(--stk-text-weak);
}

.item-delete {
  display: flex;
  font-size: 0.75em;
}

.item-delete-button {
  color: var(--stk-text-weak);

  &:hover {
    color: var(--stk-danger-60);
  }
}
</style>
